import { supabase } from './supabase';
import { getAnonId } from './anonIdentity';
import { computeWorkKey, getCurrentWorkKey } from './workKey';

/**
 * Per-user, per-day feature usage ping (signed-in users only).
 *
 * Why: paying users' feature usage was invisible — autotune renders entirely
 * client-side, GA4 is anonymous, and last_sign_in_at doesn't move while a
 * session stays alive. One row per (user, feature, UTC day) in
 * public.feature_usage answers "what do subscribers actually use?" and powers
 * dormant-Pro detection (churn/dispute early warning).
 *
 * Privacy: nothing but a feature slug and a date; anonymous users are never
 * logged. Failures are swallowed — telemetry must never break a tool.
 */
const inFlight = new Set<string>();

async function logDailyFeatureUsage(feature: string): Promise<void> {
  if (typeof window === 'undefined') return;
  let key: string | undefined;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token || !session.user?.id) return;
    key = `la_fu_v2_${session.user.id}_${feature}_${new Date().toISOString().slice(0, 10)}`;
    try {
      if (localStorage.getItem(key)) return;
    } catch { /* DB deduplication still works without browser storage. */ }
    if (inFlight.has(key)) return;
    inFlight.add(key);
    try {
      const response = await fetch('/api/feature-usage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ feature }),
      });
      // An anonymous attempt, a 401/500, or a network failure must never
      // suppress the next authenticated attempt on this device.
      if (response.ok) {
        try { localStorage.setItem(key, '1'); } catch { /* best effort */ }
      }
    } finally {
      inFlight.delete(key);
    }
  } catch { /* never let telemetry break a tool */ }
}

const workInFlight = new Set<string>();

async function logUsageWork(feature: string, rawWorkKey: string | null): Promise<void> {
  try {
    if (!rawWorkKey) return;
    // Compatibility for older callers; current exports use the same stored
    // song key as tool completions and project saves.
    const workKey = feature === 'export' && !/^[0-9a-f]{8,64}$/.test(rawWorkKey)
      ? await computeWorkKey({ name: rawWorkKey }) : rawWorkKey;
    const anonId = getAnonId();
    const session = await supabase.auth.getSession().then(({ data }) => data.session).catch(() => null);
    const signedIn = session?.access_token && session.user?.id;
    const identity = signedIn ? `user:${session.user.id}` : anonId ? `anon:${anonId}` : null;
    if (!identity) return;
    const storageKey = `la_uw_v1_${feature}_${workKey}`;
    const flightKey = `${identity}:${feature}:${workKey}`;
    const readSeen = (): string[] => {
      try {
        const value: unknown = JSON.parse(localStorage.getItem(storageKey) || '[]');
        return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];
      } catch { return []; }
    };
    // Store identities as the value so an anonymous success does not suppress
    // the authenticated attempt that also creates identity_links.
    if (readSeen().includes(identity) || workInFlight.has(flightKey)) return;
    workInFlight.add(flightKey);
    try {
      const response = await fetch('/api/usage-work', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(signedIn ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ feature, workKey, anonId }),
      });
      if (response.ok) {
        const result: { ok?: boolean; identity?: unknown } = await response.json();
        // Expired bearer tokens can succeed anonymously. Only the identity
        // actually recorded by the server is done; the user must still retry.
        if (result.ok !== true || typeof result.identity !== 'string'
          || (result.identity !== identity && result.identity !== `anon:${anonId}`)) return;
        try { localStorage.setItem(storageKey, JSON.stringify([...new Set([...readSeen(), result.identity])])); }
        catch { /* Server-side deduplication still works without storage. */ }
      }
    } finally { workInFlight.delete(flightKey); }
  } catch { /* Telemetry must never break a tool, or leave an unhandled rejection. */ }
}

/** Completed tools also count for Pro users without changing daily usage. */
export async function logCompletedWork(feature: string, workKey: string | null): Promise<void> {
  if (typeof window !== 'undefined') await logUsageWork(feature, workKey);
}

export async function logFeatureUsage(feature: string, opts?: { workKey?: string | null }): Promise<void> {
  if (typeof window === 'undefined') return;
  // Start the original daily path before touching any work-ledger dependency.
  const dailyUsage = logDailyFeatureUsage(feature);
  try {
    // Capture before awaiting; explicit null means there was no work at the
    // event, even if another file has since been selected.
    const workKey = opts?.workKey === undefined ? getCurrentWorkKey() : opts.workKey;
    await logUsageWork(feature, workKey);
  } catch { /* Work lookup/failure must never suppress daily feature_usage. */ }
  await dailyUsage;
}
